import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  QrCode,
  Download,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  UserPlus,
  ArrowRightLeft,
  Wrench,
  PackageCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate, getStatusColor } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useRealtimeUpdates } from '@/hooks/useRealtimeUpdates';
import { PermissionGate } from '@/components/PermissionGate';

interface Asset {
  id: string;
  assetId?: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  category?: { id: string; name: string; icon?: string };
  brand?: { id: string; name: string };
  vendor?: { id: string; name: string };
  branch?: { id: string; name: string };
  assignedTo?: { id: string; firstName: string; lastName: string; email: string };
  purchaseDate?: string;
  purchaseCost?: string;
}

interface FilterOption {
  id: string;
  name: string;
}

export default function AssetsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Enable real-time updates for assets
  useRealtimeUpdates(['assets']);
  
  // Filters state - initialize from URL params
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('categoryId') || '');
  const [brandId, setBrandId] = useState(searchParams.get('brandId') || '');
  const [locationId, setLocationId] = useState(searchParams.get('branchId') || '');
  const [assignmentFilter, setAssignmentFilter] = useState(searchParams.get('assigned') || '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(!!searchParams.get('status') || !!searchParams.get('categoryId') || !!searchParams.get('assigned'));
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch filter options
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get('/categories');
      return response.data.data || [];
    },
  });

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await api.get('/brands');
      return response.data.data || [];
    },
  });

  const { data: locations } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data || [];
    },
  });

  // Fetch assets
  const { data, isLoading } = useQuery({
    queryKey: ['assets', { page, pageSize, search, status, categoryId, brandId, locationId, assignmentFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pageSize === -1 ? '10000' : pageSize.toString());
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (categoryId) params.append('categoryId', categoryId);
      if (brandId) params.append('brandId', brandId);
      if (locationId) params.append('branchId', locationId);
      if (assignmentFilter) params.append('assigned', assignmentFilter);
      
      const response = await api.get(`/assets?${params.toString()}`);
      return response.data;
    },
    staleTime: 0, // Always consider data stale to ensure fresh data on navigation
    refetchOnMount: true, // Refetch when component mounts
  });

  const assets: Asset[] = data?.data || [];
  const pagination = data?.pagination;

  // Download QR code for an asset
  const downloadQRCode = async (assetId: string, assetTag: string) => {
    try {
      const response = await api.get(`/assets/${assetId}/qr`);
      const qrData = response.data.data?.qrCode || response.data.qrCode;
      if (qrData) {
        // Create download link
        const link = document.createElement('a');
        link.href = qrData;
        link.download = `QR-${assetTag}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast({
          title: 'Success',
          description: `QR code downloaded for ${assetTag}`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate QR code',
        variant: 'destructive',
      });
    }
  };

  // Bulk delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => api.delete(`/assets/${id}`)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
      toast({
        title: 'Success',
        description: `${selectedIds.size} asset(s) deleted successfully`,
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete assets',
        variant: 'destructive',
      });
    },
  });

  // Selection handlers
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === assets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assets.map(a => a.id)));
    }
  }, [assets, selectedIds.size]);

  const toggleSelect = useCallback((id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  }, [selectedIds]);

  // Export selected
  const handleExport = useCallback(() => {
    const exportData = assets
      .filter(a => selectedIds.has(a.id))
      .map(a => ({
        'Asset Tag': a.assetTag,
        'Name': a.name,
        'Serial Number': a.serialNumber || '',
        'Category': a.category?.name || '',
        'Brand': a.brand?.name || '',
        'Status': a.status,
        'Assigned To': a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : '',
        'Location': a.branch?.name || '',
        'Purchase Date': a.purchaseDate ? formatDate(a.purchaseDate) : '',
        'Purchase Cost': a.purchaseCost || '',
      }));
    
    const csv = [
      Object.keys(exportData[0] || {}).join(','),
      ...exportData.map(row => Object.values(row).map(v => `"${v}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Export Complete',
      description: `Exported ${selectedIds.size} asset(s)`,
    });
  }, [assets, selectedIds, toast]);

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setCategoryId('');
    setBrandId('');
    setLocationId('');
    setAssignmentFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || status || categoryId || brandId || locationId || assignmentFilter;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Assets</h1>
          <p className="text-muted-foreground">Manage your IT assets and equipment</p>
        </div>
        <Button asChild>
          <Link to="/assets/new">
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </Link>
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {selectedIds.size} asset(s) selected
                </span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <PermissionGate permission="assets.delete">
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete {selectedIds.size} asset(s)? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => deleteMutation.mutate(Array.from(selectedIds))}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search and Quick Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, asset tag, or serial number..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[150px]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="AVAILABLE">Available</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="DEPLOYED">Deployed</option>
                <option value="IN_STOCK">In Stock</option>
                <option value="IN_MAINTENANCE">In Maintenance</option>
                <option value="UNDER_REPAIR">Under Repair</option>
                <option value="RETIRED">Retired</option>
                <option value="DISPOSED">Disposed</option>
                <option value="LOST">Lost</option>
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                <ChevronDown className={cn("w-4 h-4 ml-2 transition-transform", showFilters && "rotate-180")} />
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            {/* Extended Filters */}
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Category</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={categoryId}
                    onChange={(e) => {
                      setCategoryId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Categories</option>
                    {(categories as FilterOption[] || []).map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Brand</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={brandId}
                    onChange={(e) => {
                      setBrandId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Brands</option>
                    {(brands as FilterOption[] || []).map((brand) => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Location</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Locations</option>
                    {(locations as FilterOption[] || []).map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Assignment</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={assignmentFilter}
                    onChange={(e) => {
                      setAssignmentFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Assets</option>
                    <option value="assigned">Assigned to Employee</option>
                    <option value="unassigned">Not Assigned (Shared/Branch)</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No assets found</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="w-12 p-4">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-input"
                        checked={selectedIds.size === assets.length && assets.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-4 font-medium">Asset Tag</th>
                    <th className="text-left p-4 font-medium">Asset ID / Name</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Brand</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Assigned To</th>
                    <th className="text-left p-4 font-medium">Location</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {assets.map((asset) => (
                    <tr 
                      key={asset.id} 
                      className={cn(
                        "hover:bg-muted/50 cursor-pointer",
                        selectedIds.has(asset.id) && "bg-primary/5"
                      )}
                      onClick={() => navigate(`/assets/${asset.id}`)}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-input"
                          checked={selectedIds.has(asset.id)}
                          onChange={() => toggleSelect(asset.id)}
                        />
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-sm font-medium text-primary">
                          {asset.assetTag}
                        </span>
                      </td>
                      <td className="p-4">
                        <div>
                          {asset.name && (
                            <p className="font-medium">{asset.name}</p>
                          )}
                          {asset.assetId && (
                            <p className={cn("font-mono text-sm", asset.name ? "text-muted-foreground" : "font-medium")}>
                              {asset.assetId}
                            </p>
                          )}
                          {!asset.name && !asset.assetId && (
                            <p className="text-muted-foreground">-</p>
                          )}
                          {asset.serialNumber && (
                            <p className="text-xs text-muted-foreground font-mono">
                              S/N: {asset.serialNumber}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1">
                          {asset.category?.icon} {asset.category?.name || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        {asset.brand?.name || '-'}
                      </td>
                      <td className="p-4">
                        <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(asset.status))}>
                          {asset.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        {asset.assignedTo ? (
                          <div>
                            <p className="font-medium">{asset.assignedTo.firstName} {asset.assignedTo.lastName}</p>
                            <p className="text-sm text-muted-foreground">{asset.assignedTo.email}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {asset.branch?.name || '-'}
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild title="View Details">
                            <Link to={`/assets/${asset.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                          {asset.status === 'AVAILABLE' || asset.status === 'IN_STOCK' ? (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              asChild 
                              title="Assign Asset"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Link to={`/assets/${asset.id}?action=assign`}>
                                <UserPlus className="w-4 h-4" />
                              </Link>
                            </Button>
                          ) : asset.status === 'ASSIGNED' || asset.status === 'DEPLOYED' ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                asChild 
                                title="Transfer Asset"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Link to={`/assets/${asset.id}?action=transfer`}>
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                asChild 
                                title="Send for Repair"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              >
                                <Link to={`/assets/${asset.id}?action=repair`}>
                                  <Wrench className="w-4 h-4" />
                                </Link>
                              </Button>
                            </>
                          ) : asset.status === 'UNDER_REPAIR' || asset.status === 'IN_MAINTENANCE' ? (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              asChild 
                              title="View Repair Status"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              <Link to={`/assets/${asset.id}?action=repair-status`}>
                                <PackageCheck className="w-4 h-4" />
                              </Link>
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="icon" asChild title="Edit Asset">
                            <Link to={`/assets/${asset.id}/edit`}>
                              <Edit className="w-4 h-4" />
                            </Link>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            title="Download QR Code"
                            onClick={() => downloadQRCode(asset.id, asset.assetTag)}
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && (
          <div className="flex items-center justify-between p-4 border-t">
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                {pageSize === -1 
                  ? `Showing all ${pagination.total} assets`
                  : `Showing ${((page - 1) * pagination.limit) + 1} to ${Math.min(page * pagination.limit, pagination.total)} of ${pagination.total} assets`
                }
              </p>
              <select
                className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                value={pageSize}
                onChange={(e) => {
                  const newSize = parseInt(e.target.value);
                  setPageSize(newSize);
                  setPage(1);
                }}
              >
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={-1}>Show All</option>
              </select>
            </div>
            {pageSize !== -1 && pagination.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
